import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useNotification } from 'src/providers/notification.provider'
import { ErrorQueryHandler } from 'src/components/error/error-query-handler.component'
import { HRCampaignService } from 'src/services/hr-campaign.service'
import { HRContactService } from 'src/services/hr-contact.service'
import { ECampaignType, THRCreateCampaignRequest } from 'src/types/hr'
import { HrPageWrapper } from '../../_components/hr-page-wrapper.component'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { ECurecallNotificationAppearance } from '@honestica/curecall-ui'
import { CampaignCreationStepper, TStep } from './_components/campaign-creation-stepper.component'
import { StepTypeSelection } from './_components/step-type-selection.component'
import { StepContactSelection } from './_components/step-contact-selection.component'
import { StepMessage } from './_components/step-message.component'
import { StepConfirmation } from './_components/step-confirmation.component'

type TCampaignDraft = {
  type: ECampaignType
  selectedContactIds: number[]
  selectedRoleValues: string[]
  selectedServiceUnitValues: string[]
  message: string
}

export default function NewHrCampaignPage() {
  const { t } = useTranslation()
  const { project } = useCurrentProject()
  const { notify } = useNotification()
  const navigate = useLocalizedNavigate()

  const [currentStep, setCurrentStep] = useState<TStep>(1)
  const [draft, setDraft] = useState<TCampaignDraft>({
    type: ECampaignType.ALERT,
    selectedContactIds: [],
    selectedRoleValues: [],
    selectedServiceUnitValues: [],
    message: '',
  })

  const {
    error: contactsError,
    data: contacts,
    isLoading: contactsLoading,
  } = useQuery({
    queryKey: ['hr-contacts', project.id],
    queryFn: () => HRContactService.getContacts(project.id),
  })

  const createCampaign = useMutation({
    mutationFn: (data: THRCreateCampaignRequest) => HRCampaignService.createCampaign(project.id, data),
    onSuccess: (campaign) => {
      notify(
        {
          title: t('pages.hr.campaigns.new.success-title'),
          message: t('pages.hr.campaigns.new.success-message'),
          appearance: ECurecallNotificationAppearance.SUCCESS,
        },
        5000,
      )
      navigate(
        `/${project.id}/hr/campaigns/${campaign.type === ECampaignType.ALERT ? 'alert' : 'plan-blanc'}/${campaign.id}`,
      )
    },
    onError: () => {
      notify(
        {
          title: t('common.actions.error'),
          message: t('common.actions.operation-failed'),
          appearance: ECurecallNotificationAppearance.ERROR,
        },
        10000,
      )
    },
  })

  const handleStep1Next = (type: ECampaignType) => {
    setDraft((prev) => ({ ...prev, type, message: prev.type !== type ? '' : prev.message }))
    setCurrentStep(2)
  }

  const handleStep2Next = (
    selectedContactIds: number[],
    selectedRoleValues: string[],
    selectedServiceUnitValues: string[],
  ) => {
    setDraft((prev) => ({ ...prev, selectedContactIds, selectedRoleValues, selectedServiceUnitValues }))
    setCurrentStep(3)
  }

  const handleStep3Next = (message: string) => {
    setDraft((prev) => ({ ...prev, message }))
    setCurrentStep(4)
  }

  const handleConfirm = () => {
    createCampaign.mutate({
      type: draft.type,
      message: draft.message,
      hrContactIds: draft.selectedContactIds,
    })
  }

  if (contactsError) {
    return <ErrorQueryHandler error={contactsError} />
  }

  return (
    <HrPageWrapper title={<CampaignCreationStepper currentStep={currentStep} />}>
      {contactsLoading && <div className="loading" />}

      {!contactsLoading && currentStep === 1 && (
        <StepTypeSelection
          initialType={draft.type}
          onNext={handleStep1Next}
          onCancel={() => navigate(`/${project.id}/hr/campaigns`)}
        />
      )}

      {!contactsLoading && currentStep === 2 && contacts && (
        <StepContactSelection
          contacts={contacts}
          initialSelectedIds={draft.selectedContactIds}
          initialRoleValues={draft.selectedRoleValues}
          initialServiceUnitValues={draft.selectedServiceUnitValues}
          onNext={handleStep2Next}
          onBack={() => setCurrentStep(1)}
        />
      )}

      {currentStep === 3 && (
        <StepMessage
          campaignType={draft.type}
          selectedCount={draft.selectedContactIds.length}
          initialMessage={draft.message}
          onNext={handleStep3Next}
          onBack={() => setCurrentStep(2)}
        />
      )}

      {currentStep === 4 && (
        <StepConfirmation
          campaignType={draft.type}
          selectedCount={draft.selectedContactIds.length}
          message={draft.message}
          isSubmitting={createCampaign.isPending}
          onConfirm={handleConfirm}
          onBack={() => setCurrentStep(3)}
        />
      )}
    </HrPageWrapper>
  )
}
